import { loadPersisted, savePersisted } from "../../../hooks/useLocalStoragePersistence";

/**
 * The order form's last delivery area, courier and order channel, kept in
 * this browser so the next new order starts on them. Saved only after an
 * order is created — a cancelled form never changes them.
 */
export interface OrderFormChoices {
    delivery_area_id: string;
    delivery_service_id: string;
    order_channel_id: string;
}

const KEY = "cps.orderForm.lastChoices";
const NONE: OrderFormChoices = { delivery_area_id: "", delivery_service_id: "", order_channel_id: "" };

export const loadLastChoices = (): OrderFormChoices => loadPersisted(KEY, NONE);

export const saveLastChoices = (choices: OrderFormChoices): void => savePersisted(KEY, choices);

/** Ids that can still be picked (active areas, couriers and channels). */
export interface AvailableChoices {
    areaIds: ReadonlySet<string>;
    serviceIds: ReadonlySet<string>;
    channelIds: ReadonlySet<string>;
}

/**
 * Which remembered choices to fill in: only fields the form left empty,
 * only ids that still exist, and the area only while no delivery charge was
 * given (so a website order or a copied order keeps its own charge).
 */
export function rememberedChoicesToApply(
    current: Partial<OrderFormChoices> & { shipping_amount?: number },
    remembered: OrderFormChoices,
    available: AvailableChoices,
): Partial<OrderFormChoices> {
    const patch: Partial<OrderFormChoices> = {};
    if (
        !current.delivery_area_id &&
        !(Number(current.shipping_amount) > 0) &&
        available.areaIds.has(remembered.delivery_area_id)
    ) {
        patch.delivery_area_id = remembered.delivery_area_id;
    }
    if (!current.delivery_service_id && available.serviceIds.has(remembered.delivery_service_id)) {
        patch.delivery_service_id = remembered.delivery_service_id;
    }
    if (!current.order_channel_id && available.channelIds.has(remembered.order_channel_id)) {
        patch.order_channel_id = remembered.order_channel_id;
    }
    return patch;
}
